import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { useDealBoard, useDealList } from '@/api/m3';
import type { DealRow, DealStage } from '@/api/m3-types';
import { Board, BoardColumn, BoardSkeleton, DealCard, StageDot } from '@/components/kit/Board';
import { Button } from '@/components/kit/Button';
import { TextLink } from '@/components/kit/Controls';
import { DateRangePicker } from '@/components/kit/DateRangePicker';
import { FilterBar, FilterChip, SearchInput } from '@/components/kit/Filters';
import { Segments } from '@/components/kit/Layout';
import { AgeChip } from '@/components/kit/Marks';
import { MetricRow } from '@/components/kit/MetricRow';
import { ErrorState, Note } from '@/components/kit/Note';
import { Avatar, Who } from '@/components/kit/Person';
import { money } from '@/lib/format';
import { ListPanel } from '@/features/base/shared/ListPanel';
import { useDebouncedSearch } from '@/features/base/shared/useDebouncedSearch';
import { useListParams } from '@/features/base/shared/useListParams';
import { CrmHeader, SectionBar, type ViewMode } from '../CrmHeader';
import { idleText, initialsOf, LOST_STAGE, nums, STAGES, stageTitle, stageTone } from '../labels';
import { OwnerChip } from '../leads/LeadsPage';
import { useM3Action } from '../useM3Submit';
import { NewDealModal } from './DealDialogs';
import { LoseByDropModal } from './DealPage';

const ALL_STAGES = [...STAGES, LOST_STAGE];

/** Подпись под клиентом: команды номеров, иначе студия; город проживания — всегда, если указан */
function dealSub(d: DealRow): string {
  const main = d.teams.length ? `${d.teams.join(', ')} · ${nums(d.activeRegs.length)}` : d.studio ? `студия «${d.studio}»` : '';
  return [main, d.city].filter(Boolean).join(' · ');
}

/**
 * Сделки (viewDeals в макете, БЛ 11.3): сегменты мероприятий, срез —
 * сделки без проигранных, прогноз, план, факт, долг. На доске первые
 * две стадии двигают перетаскиванием, три последние ставит система.
 */
export function DealsPage() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(['eventId', 'stage', 'owner', 'from', 'to', 'q', 'view'] as const);
  const mode: ViewMode = values.view === 'board' ? 'board' : 'list';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const [creating, setCreating] = useState(false);
  const [losing, setLosing] = useState<DealRow | null>(null);
  const action = useM3Action();

  const filters = { eventId: values.eventId, owner: values.owner, from: values.from, to: values.to, q: values.q };
  const list = useDealList({ ...filters, stage: mode === 'list' ? values.stage : null, page });
  const board = useDealBoard(filters, mode === 'board');
  const data = list.data;
  const open = (id: string) => navigate(`/crm/deals/${id}`);
  const filtered = Boolean(values.stage || values.owner || values.from || values.q);
  const allEvents = !values.eventId;
  const idleLimit = data?.idleLimit ?? 5;

  const drop = (stage: DealStage) => (id: string) => {
    const d = board.data?.columns.flatMap((c) => c.items).find((x) => x.id === id);
    if (!d || d.stage === stage) return;
    if (stage === 'lost') {
      setLosing(d);
      return;
    }
    action.run(() => api.post(`/deals/${id}/stage`, { stage }), `Стадия изменена: «${stageTitle(stage)}»`);
  };

  const head = (
    <tr>
      <th className="c-dcl">Клиент</th>
      {allEvents ? <th className="c-dev">Мероприятие</th> : null}
      <th className="c-dst">Стадия</th>
      <th className="r">План</th>
      <th className="r">Факт</th>
      <th className="c-didle">Без движения</th>
      <th className="c-down">Ответственный</th>
      <th>Канал</th>
    </tr>
  );

  return (
    <div className="main">
      <CrmHeader
        current="/crm/deals"
        action={
          <Button tone="primary" onClick={() => setCreating(true)}>
            ＋ Сделка
          </Button>
        }
      />

      <SectionBar
        mode={mode}
        onMode={(m) => set({ view: m === 'board' ? 'board' : null })}
        left={
          <Segments
            value={values.eventId ?? 'all'}
            onChange={(id) => set({ eventId: id === 'all' ? null : id })}
            items={[
              { value: 'all', label: 'Все', count: data?.all },
              ...(data?.events ?? []).map((e) => ({ value: e.id, label: e.label, count: e.count })),
            ]}
          />
        }
      />

      <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="ФИО, телефон, команда" />}>
        {mode === 'list' ? (
          <FilterChip
            label="Этап"
            value={values.stage}
            onChange={(stage) => set({ stage })}
            options={ALL_STAGES.map((s) => ({ value: s.key, label: s.title, side: data?.stageCounts[s.key] ?? 0 }))}
          />
        ) : null}
        <FilterChip
          label="Ответственный"
          value={values.owner}
          onChange={(owner) => set({ owner })}
          options={(data?.facets.owners ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <DateRangePicker label="Создана" from={values.from} to={values.to} onChange={(from, to) => set({ from, to })} />
      </FilterBar>

      {data ? (
        <MetricRow
          variant="slice"
          items={[
            { label: 'Сделок', value: data.metrics.count, sub: 'без проигранных' },
            { label: 'Прогноз', value: money(data.metrics.forecast), sub: 'вводит менеджер' },
            { label: 'План', value: money(data.metrics.plan), sub: 'начислено по номерам' },
            { label: 'Факт', value: money(data.metrics.fact), sub: 'оплачено' },
            { label: 'Долг', value: money(data.metrics.debt) },
          ]}
        />
      ) : null}

      {mode === 'board' ? (
        board.error ? (
          <ErrorState title="Не удалось загрузить доску." message={board.error.message} onRetry={() => void board.refetch()} />
        ) : board.isLoading || !board.data ? (
          <BoardSkeleton columns={6} />
        ) : (
          <>
            <Board columns={6}>
              {board.data.columns.map((col) => {
                const meta = ALL_STAGES.find((s) => s.key === col.stage)!;
                return (
                  <BoardColumn
                    key={col.stage}
                    title={meta.title}
                    dot={meta.tone}
                    count={col.count}
                    note={meta.auto ? 'ставится сама' : undefined}
                    // Автоматические стадии тоже принимают карточку — отказ с пояснением
                    // приходит с сервера (ТЗ M3 §11 п. 7, 409 stage_is_automatic)
                    onDrop={drop(col.stage)}
                  >
                    {col.items.length
                      ? col.items.map((d) => (
                          <DealCard
                            key={d.id}
                            id={d.id}
                            title={d.client.shortName}
                            draggable={d.draggable}
                            onOpen={() => open(d.id)}
                            meta={
                              <>
                                {allEvents ? (
                                  <>
                                    {d.event.label}
                                    <br />
                                  </>
                                ) : null}
                                {/* город проживания клиента — чтобы не открывать карточку (Road Map п. 11) */}
                                {d.city ? (
                                  <>
                                    {d.city}
                                    <br />
                                  </>
                                ) : null}
                                {d.stage === 'lost'
                                  ? d.lostReason
                                  : d.activeRegs.length
                                    ? d.activeRegs.map((r, i) => (
                                        <span key={i}>
                                          {i ? <br /> : null}
                                          {r.who} · {r.nomination}
                                        </span>
                                      ))
                                    : d.openCount
                                      ? 'в резерве'
                                      : d.studio
                                        ? `студия «${d.studio}»`
                                        : 'без студии'}
                              </>
                            }
                            footer={
                              <>
                                <span className="mny">
                                  {d.plan
                                    ? `${money(d.fact)} из ${money(d.plan)}`
                                    : d.forecast
                                      ? `прогноз ${money(d.forecast)}`
                                      : 'прогноз не задан'}
                                </span>
                                {d.stage === 'won' || d.stage === 'lost' ? null : (
                                  <AgeChip days={d.idleDays} limit={board.data!.idleLimit}>
                                    {idleText(d.idleDays)}
                                  </AgeChip>
                                )}
                                <span className="sp" />
                                <Avatar initials={d.owner ? initialsOf(d.owner.fullName) : '—'} size={22} />
                              </>
                            }
                          />
                        ))
                      : undefined}
                  </BoardColumn>
                );
              })}
            </Board>
            <Note>
              Первые две стадии менеджер двигает перетаскиванием, пока в сделке нет регистраций. Три последние система
              считает по регистрациям и платежам. Проиграть — перетащить в «Проиграна», система спросит причину.
            </Note>
          </>
        )
      ) : (
        <ListPanel
          head={head}
          cols={allEvents ? 8 : 7}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          errorTitle="Не удалось загрузить сделки."
          empty={
            data && !data.items.length ? (
              filtered ? (
                <>
                  По этим условиям сделок нет.
                  <br />
                  <TextLink onClick={() => set({ stage: null, owner: null, from: null, to: null, q: null })}>
                    Сбросить фильтры
                  </TextLink>
                </>
              ) : (
                <>Сделок пока нет — они появляются из квалифицированных заявок.</>
              )
            ) : null
          }
          paging={
            data
              ? { page, pageSize: data.pageSize, total: data.total, shown: data.items.length, onPage: (p) => set({ page: String(p) }) }
              : undefined
          }
          rows={data?.items.map((d) => (
            <tr key={d.id} className="clk" onClick={() => open(d.id)}>
              <td data-col="primary">
                <Who initials={d.client.initials} name={d.client.shortName} sub={<div className="sub">{dealSub(d)}</div>} />
              </td>
              {allEvents ? <td className="muted">{d.event.label}</td> : null}
              <td>
                <span className="stg">
                  <StageDot tone={stageTone(d.stage)} />
                  {stageTitle(d.stage)}
                </span>
                {d.stage === 'lost' && d.lostReason ? <div className="sub">{d.lostReason}</div> : null}
              </td>
              <td className="r" data-col="secondary">
                {d.plan ? (
                  <div className="num">{money(d.plan)}</div>
                ) : (
                  <>
                    <div className="num muted">{d.forecast ? money(d.forecast) : '—'}</div>
                    <div className="sub">прогноз</div>
                  </>
                )}
              </td>
              <td className="r num">{d.plan ? money(d.fact) : <span className="na">—</span>}</td>
              <td>
                {d.stage === 'lost' || d.stage === 'won' ? (
                  <span className="na">—</span>
                ) : (
                  <AgeChip days={d.idleDays} limit={idleLimit}>
                    {idleText(d.idleDays)}
                  </AgeChip>
                )}
              </td>
              <td>
                <OwnerChip owner={d.owner} none="не назначен" />
              </td>
              <td className="muted">{d.source}</td>
            </tr>
          ))}
        />
      )}

      {creating ? <NewDealModal onClose={() => setCreating(false)} /> : null}
      {losing ? <LoseByDropModal dealId={losing.id} onClose={() => setLosing(null)} /> : null}
    </div>
  );
}
